import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Receipt, Download, IndianRupee, X, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function Policy() {
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [savingBal, setSavingBal] = useState(0);
  const [tx, setTx] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmt, setPayAmt] = useState("");
  const [paying, setPaying] = useState(false);
  const [addMoneyOpen, setAddMoneyOpen] = useState(false);
  const [topupAmt, setTopupAmt] = useState("");
  const [topupMethod, setTopupMethod] = useState("upi");

  const load = async () => {
    const r = await api.get("/customer/policy");
    setP(r.data?.id ? r.data : null);
    const w = await api.get("/customer/wallet");
    setSavingBal(w.data.saving_balance);
    if (r.data?.id) {
      const t = await api.get("/customer/policy/transactions");
      setTx(t.data);
    }
  };
  useEffect(() => { load(); }, []);

  if (!p) {
    return (
      <div data-testid="policy-empty" className="min-h-[80vh] flex flex-col items-center justify-center px-6">
        <FileText className="w-16 h-16 text-slate-300" />
        <div className="font-bold text-lg mt-3">No Active Policy</div>
        <div className="text-sm text-slate-500 text-center mt-1">Contact your agent to register a policy.</div>
        <button onClick={() => nav(-1)} className="mt-6 text-tenant-primary font-bold">Go back</button>
      </div>
    );
  }

  const remaining = p.premium_amount - (p.collected_amount || 0);
  const progress = Math.min(100, Math.round(((p.collected_amount || 0) / p.premium_amount) * 100));

  const onSubmitPay = async () => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt > remaining) { toast.error(`Max ${fmtINR(remaining)} remaining`); return; }
    if (amt > savingBal) {
      // not enough saving balance — offer add money
      setPayOpen(false);
      setTopupAmt(String(Math.ceil(amt - savingBal)));
      setAddMoneyOpen(true);
      toast.warning("Insufficient saving wallet — add money first.");
      return;
    }
    setPaying(true);
    try {
      await api.post("/customer/policy/pay-premium", { amount: amt });
      toast.success(`Paid ${fmtINR(amt)} towards premium!`);
      setPayOpen(false); setPayAmt("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setPaying(false); }
  };

  const onTopup = async () => {
    const amt = parseFloat(topupAmt);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    try {
      await api.post("/customer/wallet/add-money", { amount: amt, method: topupMethod });
      toast.success(`${fmtINR(amt)} added to Saving Wallet`);
      setAddMoneyOpen(false); setTopupAmt("");
      load();
      // re-open pay modal after small delay
      setTimeout(() => setPayOpen(true), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="policy-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-6 flex items-center gap-3">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">Policy Details</div>
      </div>

      <div className="px-5 pt-6">
        <div className="bg-white border border-slate-100 rounded-3xl p-6 card-shadow">
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 rounded-xl gold-grad grid place-items-center text-tenant-primary">
              <FileText className="w-6 h-6" />
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full">{p.status}</span>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Policy Number</div>
            <div data-testid="policy-number" className="text-xl font-extrabold tracking-tight">{p.policy_number}</div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
            <Item label="Policy Name" value={p.policy_name} />
            <Item label="Premium" value={fmtINR(p.premium_amount)} />
            <Item label="Frequency" value={p.frequency.replaceAll("_", " ")} />
            <Item label="Daily Amount" value={fmtINR(p.daily_amount)} />
            <Item label="Start Date" value={fmtDate(p.start_date)} />
            {p.end_date && <Item label="End Date" value={fmtDate(p.end_date)} />}
            <Item label="Next Due" value={fmtDate(p.next_due_date || p.due_date)} />
            <Item label="Cycles Paid" value={String(p.cycles_paid ?? 0)} />
          </div>

          {p.is_overdue && (
            <div data-testid="policy-overdue-banner" className="mt-4 bg-rose-50 border border-rose-200 rounded-2xl p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
              <div className="text-xs">
                <div className="font-extrabold text-rose-700">Premium Overdue</div>
                <div className="text-rose-600">
                  Due since {fmtDate(p.next_due_date)} — Amount due now <b>{fmtINR(p.amount_due_now || 0)}</b> (includes rolled-over balance).
                </div>
              </div>
            </div>
          )}
          {!p.is_overdue && (p.amount_due_now || 0) > 0 && (
            <div data-testid="policy-due-info" className="mt-4 bg-blue-50 border border-blue-200 rounded-2xl p-3 text-xs">
              <span className="font-extrabold text-blue-700">Next premium:</span>{" "}
              <span className="text-blue-700">{fmtINR(p.amount_due_now)} by {fmtDate(p.next_due_date)}</span>
            </div>
          )}

          <div className="mt-5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-slate-700">Collection Progress</span>
              <span className="font-extrabold text-tenant-primary">{progress}%</span>
            </div>
            <div className="mt-2 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full emerald-grad transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex items-center justify-between text-[11px] text-slate-500 mt-2">
              <span>Collected {fmtINR(p.collected_amount || 0)}</span>
              <span>Remaining {fmtINR(remaining)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow mt-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Saving Wallet</div>
            <div className="font-extrabold text-emerald-600">{fmtINR(savingBal)}</div>
          </div>
          <button data-testid="open-pay-modal" onClick={() => { setPayAmt(String(Math.min(remaining, savingBal) || remaining)); setPayOpen(true); }} className="bg-tenant-primary text-white px-5 py-2.5 rounded-xl font-bold text-sm inline-flex items-center gap-2 tap-scale">
            <Receipt className="w-4 h-4" /> Pay Premium
          </button>
        </div>

        {/* Policy Document */}
        <div className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow mt-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Policy Document</div>
              <div className="font-bold text-sm">{p.document_name || "Policy PDF"}</div>
            </div>
            {p.document_url ? (
              <a href={p.document_url} target="_blank" rel="noreferrer" data-testid="download-policy-doc" className="bg-emerald-600 text-white px-4 py-2 rounded-xl font-bold text-sm inline-flex items-center gap-2 tap-scale">
                <Download className="w-4 h-4" /> Download
              </a>
            ) : (
              <span className="text-xs text-slate-400 italic">Not uploaded yet</span>
            )}
          </div>
        </div>

        {/* Policy Transactions */}
        <div className="mt-5">
          <h3 className="font-extrabold tracking-tight mb-3 inline-flex items-center gap-2">
            <History className="w-4 h-4" /> Policy Transactions ({tx.length})
          </h3>
          <div className="space-y-2.5">
            {tx.map((t) => (
              <div key={t.id} data-testid={`pol-tx-${t.id}`} className="bg-white border border-slate-100 rounded-2xl p-3 flex items-center justify-between card-shadow">
                <div>
                  <div className="font-bold text-sm capitalize">{t.category.replaceAll("_", " ")}</div>
                  <div className="text-[11px] text-slate-400">{fmtDate(t.created_at)}</div>
                  {t.note && <div className="text-[11px] text-slate-500 mt-0.5">{t.note}</div>}
                </div>
                <div className={`font-extrabold ${t.type === "premium_payment" || t.type === "wallet_debit" ? "text-rose-500" : "text-emerald-600"}`}>
                  {t.type === "premium_payment" || t.type === "wallet_debit" ? "-" : "+"}{fmtINR(t.amount)}
                </div>
              </div>
            ))}
            {tx.length === 0 && <div className="text-center text-slate-400 text-sm py-6">No policy transactions yet.</div>}
          </div>
        </div>
      </div>

      {/* Pay Premium Modal */}
      {payOpen && (
        <div data-testid="pay-modal" className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setPayOpen(false)}>
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-extrabold text-lg">Pay Premium</div>
              <button onClick={() => setPayOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-50 rounded-xl p-2.5">
                <div className="text-slate-400 font-bold">Remaining</div>
                <div className="font-extrabold">{fmtINR(remaining)}</div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-2.5">
                <div className="text-slate-400 font-bold">Saving Wallet</div>
                <div className="font-extrabold text-emerald-600">{fmtINR(savingBal)}</div>
              </div>
            </div>

            <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount to Pay</label>
            <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-3">
              <IndianRupee className="w-5 h-5 text-slate-400" />
              <input data-testid="pay-amount-input" value={payAmt} onChange={(e) => setPayAmt(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" inputMode="decimal" className="flex-1 outline-none text-xl font-extrabold" />
              <button onClick={() => setPayAmt(String(remaining))} className="text-[10px] bg-slate-100 text-slate-600 font-bold px-2 py-1 rounded">FULL</button>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {[500, 1000, 5000].map((q) => (
                <button key={q} data-testid={`pay-quick-${q}`} onClick={() => setPayAmt(String(q))} className="bg-slate-100 rounded-lg py-1.5 text-xs font-bold">₹{q}</button>
              ))}
            </div>

            {parseFloat(payAmt) > savingBal && (
              <div className="mt-3 bg-amber-50 text-amber-700 rounded-xl p-3 text-xs flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Insufficient saving wallet by <b>{fmtINR(parseFloat(payAmt) - savingBal)}</b>. Tap Pay to add money first.</span>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => setPayOpen(false)} className="py-3 rounded-xl border border-slate-200 font-bold">Cancel</button>
              <button data-testid="confirm-pay-premium" onClick={onSubmitPay} disabled={paying} className="py-3 rounded-xl bg-tenant-primary text-white font-bold disabled:opacity-60">{paying ? "Processing..." : "Pay Now"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Money Modal (fallback when saving insufficient) */}
      {addMoneyOpen && (
        <div data-testid="topup-modal" className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setAddMoneyOpen(false)}>
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-extrabold text-lg">Add Money to Saving</div>
              <button onClick={() => setAddMoneyOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-500 mt-1">Top up to pay full premium</div>

            <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount</label>
            <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-3">
              <IndianRupee className="w-5 h-5 text-slate-400" />
              <input data-testid="topup-amount" value={topupAmt} onChange={(e) => setTopupAmt(e.target.value.replace(/[^\d.]/g, ""))} className="flex-1 outline-none text-xl font-extrabold" />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              {["upi", "gpay", "phonepe", "razorpay"].map((m) => (
                <button key={m} data-testid={`topup-method-${m}`} onClick={() => setTopupMethod(m)} className={`py-2 rounded-xl text-xs font-bold uppercase ${topupMethod === m ? "bg-tenant-primary text-white" : "bg-slate-100"}`}>
                  {m}
                </button>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => setAddMoneyOpen(false)} className="py-3 rounded-xl border border-slate-200 font-bold">Cancel</button>
              <button data-testid="confirm-topup" onClick={onTopup} className="py-3 rounded-xl bg-emerald-600 text-white font-bold">Add & Continue</button>
            </div>
          </div>
        </div>
      )}

      <div className="h-10" />
    </div>
  );
}

const Item = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">{label}</div>
    <div className="font-bold text-slate-800 capitalize">{value}</div>
  </div>
);
